/**
 * useVisibilityReconnect Hook
 *
 * Lightweight safety net for long tab suspensions (10+ minutes).
 * Increments a global refresh key so RTDB hooks can re-subscribe.
 *
 * Firebase SDK v11.x handles Firestore reconnection on its own via
 * persistent cache and auto-reconnect, so this hook does NOT touch
 * the Firestore network or force token refreshes.
 *
 * Usage:
 *   // Call once in AuthenticatedLayout (app root)
 *   useVisibilityReconnect();
 */

import { useEffect, useRef } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { logger } from '../utils/logger';

interface UseVisibilityReconnectOptions {
  /** Minimum time (ms) tab must be hidden before triggering refresh key increment. Default: 600000 (10 minutes) */
  minHiddenDuration?: number;
  /** Whether to enable the hook. Default: true */
  enabled?: boolean;
}

/**
 * Global hook for handling tab visibility changes after long suspensions.
 * Should be called once at the app root level (AuthenticatedLayout).
 */
export const useVisibilityReconnect = (options: UseVisibilityReconnectOptions = {}) => {
  const {
    minHiddenDuration = 600000, // 10 minutes
    enabled = true,
  } = options;

  const { incrementVisibilityRefreshKey } = useFirebaseStore();
  const hiddenAtRef = useRef<number | null>(null);

  // Use refs for values that handlers need at runtime
  // This allows us to have a stable effect that doesn't re-run when these change
  const minHiddenDurationRef = useRef(minHiddenDuration);
  const incrementRefreshKeyRef = useRef(incrementVisibilityRefreshKey);

  // Keep refs in sync with current values
  useEffect(() => {
    minHiddenDurationRef.current = minHiddenDuration;
    incrementRefreshKeyRef.current = incrementVisibilityRefreshKey;
  }, [minHiddenDuration, incrementVisibilityRefreshKey]);

  // Set up event listeners once (stable effect)
  useEffect(() => {
    if (!enabled) return;

    /**
     * Handle visibility change - track when tab becomes hidden and trigger
     * refresh key increment when it becomes visible again after sufficient time.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenAt = hiddenAtRef.current;
        const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0;

        if (hiddenDuration >= minHiddenDurationRef.current) {
          // Clear immediately to prevent duplicate triggers from the focus event backup
          hiddenAtRef.current = null;

          logger.log(`[Visibility] Long suspension detected (${Math.round(hiddenDuration / 1000)}s)`);
          incrementRefreshKeyRef.current();
        }

        hiddenAtRef.current = null;
      }
    };

    /**
     * Handle window focus - some browsers don't fire visibilitychange reliably,
     * so we also listen for focus as a backup.
     */
    const handleFocus = () => {
      if (hiddenAtRef.current) {
        const hiddenDuration = Date.now() - hiddenAtRef.current;
        if (hiddenDuration >= minHiddenDurationRef.current) {
          // Let visibilitychange handler do the work if it fires,
          // but set a short timeout to trigger if it doesn't
          setTimeout(() => {
            if (hiddenAtRef.current) {
              handleVisibilityChange();
            }
          }, 100);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled]); // Only re-run if enabled changes
};

export default useVisibilityReconnect;
